import React from "react";
import { useQuery } from "react-query";
import useInsightsDrilldown from "hooks/useInsightsDrilldown";
import { getOrgTimeHistory } from "utils/insights";
import { notCountedNote } from "utils/notCountedNote";
import {
  AverageRatingCard,
  MeetingCostCard,
  MetricCard,
  TimeTrend,
  hours,
  number,
} from "./SummaryCards";
import css from "./OrganizationSummary.module.scss";

export default function OrganizationSummary({
  data,
  period,
  self,
  uid,
  isLoading,
  isError,
}) {
  const { openDrilldown } = useInsightsDrilldown();
  const {
    data: history = [],
    isError: historyError,
    isPreviousData: historyPending,
  } = useQuery({
    queryKey: ["OrgTimeHistory", uid, data?.snapshotDate, period],
    queryFn: () => getOrgTimeHistory(uid, data.snapshotDate, period),
    enabled: !!uid && !!data?.snapshotDate,
    staleTime: 5 * 60 * 1000,
    keepPreviousData: true,
  });
  const time = data?.timeSplit?.[period]?.[self];
  const rating = data?.effectiveness?.[period]?.[self];
  const cost = data?.meetingCost?.[period]?.[self];
  // Flights, travel and personal entries the period left out. Shown in the
  // popups that list the whole period, not in internal or external ones.
  const notCounted = notCountedNote(cost?.notCounted);
  const open = (name, key, meetingType = "All", type = "Meetings", note = null) =>
    openDrilldown({
      name,
      type,
      meetingType,
      redisKey: `users:${uid}:orgCalculations:${key}`,
      ...(note ? { note } : {}),
    });
  const timeValues = (field) =>
    history.map((item) => {
      const value = item?.[self]?.[field];
      return number(value) ? Math.max(0, value) : null;
    });
  if (isError) return <p role="alert">Unable to load organization summary.</p>;
  return (
    <div className={css.grid}>
      <MetricCard title="Meeting time" loading={isLoading}>
        <button
          className={css.value}
          disabled={!uid || !number(time?.meetingHrs)}
          onClick={() =>
            open(
              "Meeting Time Details",
              `timeSplit:${period}:${self}:totalMeetingDetails`,
              "All",
              "Meetings",
              notCounted,
            )
          }
        >
          {hours(time?.meetingHrs)}
        </button>
        <TimeTrend
          label="Meeting time"
          historyPending={historyPending}
          disabled={!uid || !number(time?.meetingHrs)}
          onClick={() =>
            open(
              "Meeting Time Details",
              `timeSplit:${period}:${self}:totalMeetingDetails`,
              "All",
              "Meetings",
              notCounted,
            )
          }
          values={timeValues("meetingHrs")}
          current={time?.meetingHrs}
        />
        {historyError && <div className={css.caption}>Trend unavailable</div>}
      </MetricCard>
      <MetricCard
        title="Focus time"
        loading={isLoading}
        description="Estimated working hours minus meeting hours, not measured focus sessions."
      >
        <div className={css.value}>{hours(time?.meHrs)}</div>
        <TimeTrend
          label="Focus time"
          historyPending={historyPending}
          increaseIsGood
          values={timeValues("meHrs")}
          current={number(time?.meHrs) ? Math.max(0, time.meHrs) : undefined}
        />
        <div className={css.caption}>
          {time?.meHrs < 0
            ? "Meetings exceed estimated working hours"
            : "Estimated non-meeting time"}
        </div>
      </MetricCard>
      <AverageRatingCard
        rating={rating}
        loading={isLoading}
        uid={uid}
        onOpenStars={(stars) =>
          open(
            `${stars} star ratings`,
            `effectiveness:${period}:${self}:${stars}star`,
            "Rated",
            "Effectiveness Rating",
          )
        }
      />
      <MeetingCostCard
        cost={cost}
        loading={isLoading}
        uid={uid}
        history={timeValues("totalMeetingsCost")}
        historyPending={historyPending}
        onOpen={(bucket, meetingType) =>
          open(
            "Cost of meetings",
            `${period}:${self}:${bucket}MeetingsCostIds`,
            meetingType,
            "Meetings",
            bucket === "total" ? notCounted : null,
          )
        }
      />
    </div>
  );
}
